// Imports
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import TooltipHorizon from "widgets/controls/tooltip";
import { t } from "i18next";
import { CONST } from "models/constants";
import { deviceStatusIcon, deviceVerifiedIcon } from "providers/icons";
import { notifySimple } from "providers/toast";
import { MdCopyAll, MdPlayArrow } from "react-icons/md";
import { shallowEqual, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { formatDate } from "util/datelib";
import { getReadings } from "util/device";
import MoreAction, { DropDownItem } from "widgets/controls/moreAction";
import CircleButton from "widgets/controls/buttons/circle-button";

// Define the structure of the JdTableModel
export class DevicePageTableModel {
  id: string;
  snr: string;
  model: string;
  classes: string[];
  deployed: boolean;
  lastComms: string;
  created: string;
  host: string;
  name: string;
  meterNumber?: string;
  waterMeter?: number;
  tempMeter?: number;
  electricalMeter?: number;
  forwardticks?: number;
  signal?: number;
  battery?: number;
  installOdo?: number;
  ratio?: number;
  installReading?: number;
  health: string;
  actions: string;
  postPaid?: boolean;
  userCredit?: number;
  level?: number;
  sensorHeight?: number;
  overflowHeight?: number;
  meterReading?: number;
  dataQuality: string;
  address?: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  installerName?: string;
  installDate?: string;
  meterModel?: string;
  dailyUsageLimit?: number;
  photo1?: string;
  photo2?: string;
  photo3?: string;
  recipients: [];
  installLocation?: [];
  lowLevelPercentage?: number;
  valveState?: string;
  correctiveReading?: number;
  correctiveOdo?: number;
  cumulativeOdo?: number;
  usercredit?: number;
  contactorstate?: string;
  tokenPushEnabled?: boolean;
}
const defineDpTablecolumns = (
  onEditClick?: (info: CellContext<DevicePageTableModel, any>) => void,
  onDataQualityClick?: (info: CellContext<DevicePageTableModel, any>) => void,
  onDevToolsClick?: (info: CellContext<DevicePageTableModel, any>) => void,
  onRemoveClick?: (info: CellContext<DevicePageTableModel, any>) => void,
  options?: { hideActions?: boolean; canSwitchContactor?: boolean },
  onPlayTokenClick?: (info: CellContext<DevicePageTableModel, any>) => void,
  onSwitchContactorClick?: (
    info: CellContext<DevicePageTableModel, any>
  ) => void
) => {
  // Initialize the translation hook and
  const moreMenuItems: DropDownItem[] = [
    {
      label: t("button.action.view"),
      active: true,
      onSelected: onEditClick,
    },
    {
      label: t("button.action.data.quality"),
      active: true,
      onSelected: onDataQualityClick,
      permissionRole: CONST.ROLE_SUPER,
    },
    {
      label: t("button.action.dev.tools"),
      active: true,
      onSelected: onDevToolsClick,
      permissionRole: CONST.ROLE_SUPER,
    },
    {
      label: t("button.action.remove"),
      active: true,
      onSelected: onRemoveClick,
      permissionRole: CONST.ROLE_MANAGER,
    },
  ];

  const copyToClipboard = (snr: string) => {
    navigator.clipboard.writeText(snr).then(
      () => {
        notifySimple(t("notification.copied.to.clipboard"), "success");
      },
      (err) => {
        notifySimple(t("notification.failed.to.copy"), "error");
      }
    );
  };
  // create a column helper for the JdTableModel
  const columnHelper = createColumnHelper<DevicePageTableModel>();

  // Define the columns for the JdTable
  const devicePageTablecolumns = [
    // Reference column
    columnHelper.accessor("snr", {
      enableSorting: true,
      meta: { rowClick: false },
      header: () => <span className="">{t("table.header.device.snr")}</span>,
      cell: (info): JSX.Element => {
        const { verifiedIcon } = deviceVerifiedIcon(
          info.row.original.dataQuality || null
        );

        return (
          <span className="flex flex-grow items-center gap-2 truncate">
            <TooltipHorizon
              extra="custom-class"
              trigger={verifiedIcon}
              content={<span>{`${info.row.original.dataQuality}`}</span>}
              placement="bottom"
            />
            <span className="">{info.getValue()}</span>
            <MdCopyAll
              onClick={() => copyToClipboard(info.getValue())}
              className="cursor-pointer"
            />
          </span>
        );
      },
    }),

    // Description column
    columnHelper.accessor("host", {
      enableSorting: true,
      meta: { rowClick: true },
      header: () => (
        <span className="">{t("table.header.device.address")}</span>
      ),
      cell: (info) => <span className="">{info.getValue()}</span>,
    }),

    // Description column
    columnHelper.accessor("meterNumber", {
      enableSorting: true,
      meta: { rowClick: true },
      header: () => (
        <span className="">{t("table.header.device.meter.number")}</span>
      ),
      cell: (info) => <span className="">{info.getValue()}</span>,
    }),

    columnHelper.accessor("lastComms", {
      enableSorting: true,
      meta: { rowClick: true },
      header: () => (
        <span className="">{t("table.header.device.lastcomms")}</span>
      ),
      cell: (info) => (
        <span className="">{formatDate(info.getValue(), "lastcomms")}</span>
      ),
    }),

    columnHelper.accessor("waterMeter", {
      enableSorting: false,
      meta: { rowClick: true },
      header: () => (
        <span className="">{t("table.header.device.reading")}</span>
      ),
      cell: (info) => (
        <span className="">{getReadings(info.row.original)}</span>
      ),
    }),

    columnHelper.accessor("health", {
      enableSorting: false,
      meta: { rowClick: false },
      header: () => <span className="">{t("table.header.device.health")}</span>,
      cell: (info): JSX.Element => {
        const model = info.row.original.model;
        const {
          batteryIcon,
          signalIcon,
          valveIcon,
          lastCommsIcon,
          userCreditIcon,
          contacterIcon,
        } = deviceStatusIcon(
          info.row.original.battery || null,
          info.row.original.signal || null,
          info.row.original.valveState || null,
          info.row.original.lastComms || null,
          info.row.original.usercredit || null,
          info.row.original.contactorstate || null
        );

        return (
          <span className="flex flex-grow items-center gap-2 truncate">
            {model === "lora.gateway" && <span>{lastCommsIcon}</span>}
            {model !== "tag.opt" && <span>{batteryIcon}</span>}
            <TooltipHorizon
              extra="custom-class"
              trigger={userCreditIcon}
              content={<span>{`${t("table.header.device.user.credit")}`}</span>}
              placement="bottom"
            />
            <TooltipHorizon
              extra="custom-class"
              trigger={signalIcon}
              content={<span>{`${info.row.original.signal}`}</span>}
              placement="bottom"
            />
            <TooltipHorizon
              extra="custom-class"
              trigger={valveIcon}
              content={<span>{`${info.row.original.valveState}`}</span>}
              placement="bottom"
            />
            <TooltipHorizon
              extra="custom-class"
              trigger={
                <span
                  onClick={() => {
                    if (options?.canSwitchContactor) {
                      onSwitchContactorClick(info);
                    }
                  }}
                  className="cursor-pointer"
                >
                  {contacterIcon}
                </span>
              }
              content={<span>{`${info.row.original.contactorstate}`}</span>}
              placement="bottom"
            />

            {(info.row.original?.contactorstate ||
              info.row.original?.tokenPushEnabled) && (
              <CircleButton
                onClick={() => onPlayTokenClick(info)}
                icon={MdPlayArrow}
                size="xs"
                variant="outline"
                tooltip={t("tooltip.play.token")}
                tooltipPlacement="top"
              />
            )}
          </span>
        );
      },
    }),

    columnHelper.accessor("classes", {
      enableSorting: false,
      meta: { rowClick: true },
      header: () => <span className="">{t("table.header.device.sites")}</span>,
      cell: (info) => (
        <span className="flex flex-grow flex-row gap-1 truncate">
          {info.getValue().map((item) => {
            const sites = useSelector(accountSiteList, shallowEqual);
            const site = sites.find((a) => a.id === item);
            return site ? site.name : null;
          })}
        </span>
      ),
    }),

    // Actions column
    !options?.hideActions &&
      columnHelper.accessor("actions", {
        enableSorting: false,
        meta: {
          customClass: "justify-items-center",
          customHeadClass: "justify-items-end",
        },
        header: () => <span className="">{t("table.header.actions")}</span>,
        cell: (info) => (
          <div>
            <MoreAction
              items={moreMenuItems}
              info={info}
              title={t("button.action.device.options")}
              extra="w-full flex"
            />
          </div>
        ),
      }),
  ].filter(Boolean);

  return devicePageTablecolumns;
};

export default defineDpTablecolumns;
